import { CSSProperties } from 'react'

import { IconX } from '@tabler/icons-react'
import styled, { css } from 'styled-components'

import { HEIGHTS } from './Button'

import { useLocale } from '@/context/localeContext'

import { buildPassTheme } from 'src/styles/theme'

type Size = 'small' | 'medium' | 'large'

type WrapperProps = {
  size?: Size
  whiteBg?: boolean
  capitalise?: boolean
  hasIcon: boolean
}

const Wrapper = styled.div<WrapperProps>`
  ${({ theme }) => theme.font.caption01}
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary._600};
  background-color: ${({ whiteBg, theme }) =>
    whiteBg ? theme.colour.neutral.white : 'transparent'};
  height: ${({ size }) => HEIGHTS[size || 'small']};
  padding-left: ${({ hasIcon }) => (hasIcon ? '0.5rem' : '0.625rem')};
  padding-right: 0.625rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colour.neutral.border};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  text-transform: ${({ capitalise }) => (capitalise ? 'uppercase' : 'initial')};
  white-space: nowrap; // we don't really ever want these breaking. If you need to, best to optionally truncate via prop?
`

type IconProps = {
  iconColour?: string
  haloColour?: string
}

const IconWrapper = styled.span<IconProps>`
  position: relative;
  display: inline-flex;

  ${({ iconColour }) =>
    iconColour &&
    css`
      svg {
        position: relative;
        color: ${iconColour};
      }
    `}

  ${({ haloColour }) =>
    haloColour &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: -0.125rem;
        left: -0.125rem;
        width: calc(100% + 0.25rem);
        height: calc(100% + 0.25rem);
        border-radius: 50%;
        background-color: ${haloColour};
      }
    `}
`

const CloseWrapper = styled.span`
  cursor: pointer;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  color: ${({ theme }) => theme.colour.secondary._300};
  background-color: ${({ theme }) => theme.colour.neutral.surface};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const getColours = (
  variant?: Variant
): { iconColour: string; haloColour: string } => {
  if (variant === 'info')
    return {
      iconColour: buildPassTheme.colour.semantic.successDark,
      haloColour: buildPassTheme.colour.semantic.successLight,
    }

  if (variant === 'warning')
    return {
      iconColour: buildPassTheme.colour.semantic.warning,
      haloColour: buildPassTheme.colour.semantic.warningLight,
    }

  if (variant === 'danger')
    return {
      iconColour: buildPassTheme.colour.semantic.errorDark,
      haloColour: buildPassTheme.colour.semantic.errorLight,
    }

  if (variant === 'success')
    return {
      iconColour: buildPassTheme.colour.primary._600,
      haloColour: buildPassTheme.colour.primary._50,
    }

  if (variant === 'ai')
    return {
      iconColour: buildPassTheme.colour.ai.dark,
      haloColour: buildPassTheme.colour.ai.light,
    }

  if (variant === 'blue')
    return {
      iconColour: '#3bbad0',
      haloColour: '#e0f6fa',
    }

  return {
    iconColour: buildPassTheme.colour.secondary._800,
    haloColour: null,
  }
}

type Variant = 'info' | 'warning' | 'success' | 'danger' | 'ai' | 'blue'

export type Props = {
  label: React.ReactNode
  icon?: React.ReactNode
  size?: Size
  className?: string
  variant?: Variant
  style?: CSSProperties
  whiteBg?: boolean
  capitalise?: boolean
  closeable?: () => void
}

export type PillProps = Props

export const Pill: React.FC<Props> = ({
  label,
  icon,
  size = 'small',
  variant,
  whiteBg,
  className,
  capitalise = true,
  closeable,
  style,
  ...rest // for dropdown and popover reasons
}) => {
  const { t } = useLocale()
  const { iconColour, haloColour } = getColours(variant)

  return (
    <Wrapper
      className={className}
      size={size}
      whiteBg={whiteBg}
      capitalise={capitalise}
      hasIcon={!!icon}
      style={style}
      {...rest}
    >
      {icon && (
        <IconWrapper iconColour={iconColour} haloColour={haloColour}>
          {icon}
        </IconWrapper>
      )}
      {typeof label === 'string' ? t(label) : label}
      {closeable && (
        <CloseWrapper onClick={closeable}>
          <IconX size={14} />
        </CloseWrapper>
      )}
    </Wrapper>
  )
}

Pill.displayName = 'Pill'
